// The 24/7 Open House - Main Interaction Engine

document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initMobileMenu();
  initDashboardTabs();
  initEcosystemSelector();
  initSmsSimulator();
  initFaqAccordion();
  initModals();
  initScrollReveal();
});

/* ==========================================================================
   Scroll Reveal Animations
   ========================================================================== */
function initScrollReveal() {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Groups of siblings that should stagger in together
  const groupSelectors = [
    '.problem-grid > .problem-card',
    '.pipeline-grid > .pipeline-step',
    '.features-grid > .feature-card',
    '.pricing-features-grid > .pricing-feature-item',
    '.faq-accordion > .faq-item'
  ];

  groupSelectors.forEach(selector => {
    document.querySelectorAll(selector).forEach((el, i) => {
      el.classList.add('reveal-up');
      el.style.setProperty('--reveal-delay', `${Math.min(i * 90, 450)}ms`);
    });
  });

  // Standalone elements that fade/rise in individually
  const soloSelectors = [
    '.section-header',
    '.pricing-card',
    '.mini-stat-card',
    '.hud-card',
    '.dashboard-card',
    '.channel-detail-card',
    '.hero-mobile-qr-card'
  ];

  soloSelectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(el => {
      if (!el.classList.contains('reveal-up')) {
        el.classList.add('reveal-up');
      }
    });
  });

  const revealEls = document.querySelectorAll('.reveal-up, .reveal-fade, .reveal-scale');
  if (!revealEls.length) return;

  if (reduceMotion || !('IntersectionObserver' in window)) {
    revealEls.forEach(el => el.classList.add('in-view'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in-view');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.15,
    rootMargin: '0px 0px -60px 0px'
  });

  revealEls.forEach(el => observer.observe(el));
}

/* ==========================================================================
   Header Sticky & Scrolled State
   ========================================================================== */
function initHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const handleScroll = () => {
    if (window.scrollY > 40) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll();
}

/* ==========================================================================
   Mobile Navigation Drawer
   ========================================================================== */
function initMobileMenu() {
  const toggleBtn = document.querySelector('.mobile-toggle');
  const drawer = document.querySelector('.mobile-drawer');
  const drawerLinks = document.querySelectorAll('.mobile-drawer .nav-link, .mobile-drawer .btn');

  if (!toggleBtn || !drawer) return;

  const toggle = (open) => {
    const isOpen = open !== undefined ? open : !drawer.classList.contains('open');
    drawer.classList.toggle('open', isOpen);
    toggleBtn.classList.toggle('active', isOpen);
    document.body.style.overflow = isOpen ? 'hidden' : '';
  };

  toggleBtn.addEventListener('click', () => toggle());

  drawerLinks.forEach(link => {
    link.addEventListener('click', () => toggle(false));
  });
}

/* ==========================================================================
   Interactive Platform Dashboard Tabs
   ========================================================================== */
function initDashboardTabs() {
  const tabBtns = document.querySelectorAll('.platform-tab-btn');
  const tabViews = document.querySelectorAll('.tab-view');

  if (!tabBtns.length) return;

  tabBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const targetId = btn.getAttribute('data-tab');

      tabBtns.forEach(b => b.classList.remove('active'));
      tabViews.forEach(v => v.classList.remove('active'));

      btn.classList.add('active');
      const targetView = document.getElementById(targetId);
      if (targetView) {
        targetView.classList.add('active');
      }
    });
  });
}

/* ==========================================================================
   Multi-Channel Ecosystem Orbit Selector
   ========================================================================== */
const channelData = {
  signs: {
    title: 'Yard Signs & Alert Riders',
    desc: 'Prospective buyers driving past your listing see your dedicated toll-free number and custom extension rider. When they call for the price and audio tour, our patented call-capture protocol instantly captures their verified phone number and name before transferring the call.',
    tags: ['Patented Protocol', 'Instant Caller ID', 'Curbside Capture', '24/7 Availability']
  },
  social: {
    title: 'Social Media Campaigns & Ads',
    desc: 'Run targeted Facebook, Instagram, and TikTok ads highlighting your exclusive audio listing extension. Interested buyers call or scan your QR code directly from their feeds, immediately entering your CRM and automated 3-day SMS sequence.',
    tags: ['Instagram & Facebook', 'High Conversion', 'Automated SMS Sync', 'Lead Notifications']
  },
  qr: {
    title: 'Dynamic QR Codes',
    desc: 'Display high-resolution QR codes on window signs, flyers, postcards, and open house standees. When scanned, shoppers instantly dial into the listing extension without typing numbers, generating zero-friction verified inbound leads.',
    tags: ['Instant Dialing', 'Print & Digital', 'Zero Friction', 'Scan Analytics']
  },
  mail: {
    title: 'Direct Mail & "Just Listed" Postcards',
    desc: 'Send targeted neighborhood mailers featuring your toll-free extension: "Call 1-800-XXX-XXXX Ext 102 for instant price and full audio details 24/7." Track exactly which neighborhoods yield active move-up buyers.',
    tags: ['Targeted Farming', 'Call Tracking', 'Exclusive Leads', 'No Competition']
  },
  ads: {
    title: 'Digital Advertising & Local Portals',
    desc: 'Feature your toll-free extension across Google Search, local real estate classifieds, and listing portals. Prospects call you directly instead of being routed to competing premier agents on large portal aggregators.',
    tags: ['Google Search', 'Direct Inbound', 'Shield Your Leads', 'Toll-Free Authority']
  },
  flyers: {
    title: 'Property Flyers & Builder Partnerships',
    desc: 'Partner with local builders and open house hosts. Distribute spec sheets with 24/7 Open House call-capture numbers. You receive exclusive buyer leads for every single inquiry generated from the flyer box.',
    tags: ['Builder Partnerships', 'Open House Handouts', 'Leverage Other Listings', '100% Exclusive']
  }
};

function initEcosystemSelector() {
  const nodes = document.querySelectorAll('.sat-node');
  const titleEl = document.getElementById('channel-title');
  const descEl = document.getElementById('channel-desc');
  const tagsEl = document.getElementById('channel-tags');

  if (!nodes.length || !titleEl) return;

  nodes.forEach(node => {
    node.addEventListener('click', () => {
      nodes.forEach(n => n.classList.remove('active'));
      node.classList.add('active');

      const channelKey = node.getAttribute('data-channel');
      const data = channelData[channelKey];
      if (!data) return;

      titleEl.innerHTML = data.title;
      descEl.textContent = data.desc;

      if (tagsEl) {
        tagsEl.innerHTML = data.tags.map(t => `<span class="channel-tag">${t}</span>`).join('');
      }
    });
  });
}

/* ==========================================================================
   Automated SMS Simulator
   ========================================================================== */
function initSmsSimulator() {
  const stream = document.getElementById('sms-chat-stream');
  const restartBtn = document.getElementById('btn-replay-sms');

  if (!stream || !restartBtn) return;

  const script = [
    { type: 'inbound', text: 'Inbound Call to Ext #102: (386) 555-0192 has listened to 742 Evergreen Terr audio tour.', time: 'Just now' },
    { type: 'outbound', text: 'Hi! Thank you for inquiring about 742 Evergreen Terr. View full specs, photos & floor plans here: 247open.me/e102', time: 'Minute 1' },
    { type: 'inbound', text: 'Alert to Agent: Prospective buyer (386) 555-0192 is actively reviewing 742 Evergreen Terr. Press here to call back directly.', time: 'Minute 2' },
    { type: 'outbound', text: 'Day 2 Follow-Up: "Hi there, have you had a chance to view the photos? Let me know if you’d like to see the newly remodeled kitchen in person!"', time: 'Day 2' },
    { type: 'outbound', text: 'Day 3 Follow-Up: "Private walkthroughs for 742 Evergreen Terr are happening this weekend. Would 11am or 2pm work best for you?"', time: 'Day 3' }
  ];

  let step = 0;
  let interval;

  const playNext = () => {
    if (step >= script.length) {
      clearInterval(interval);
      return;
    }

    const item = script[step];
    const msg = document.createElement('div');
    msg.className = `sms-msg ${item.type === 'inbound' ? 'sms-inbound' : 'sms-outbound'}`;
    msg.innerHTML = `${item.text} <span class="sms-timestamp">${item.time}</span>`;
    stream.appendChild(msg);
    stream.scrollTop = stream.scrollHeight;
    step++;
  };

  const restart = () => {
    clearInterval(interval);
    stream.innerHTML = '';
    step = 0;
    playNext();
    interval = setInterval(playNext, 1800);
  };

  restartBtn.addEventListener('click', restart);
}

/* ==========================================================================
   FAQ Accordion
   ========================================================================== */
function initFaqAccordion() {
  const items = document.querySelectorAll('.faq-item');

  items.forEach(item => {
    const question = item.querySelector('.faq-question');
    if (!question) return;

    question.addEventListener('click', () => {
      const isActive = item.classList.contains('active');

      // Close all other items
      items.forEach(other => {
        if (other !== item) other.classList.remove('active');
      });

      // Toggle current
      item.classList.toggle('active', !isActive);
    });
  });
}

/* ==========================================================================
   Modals (Terms, Privacy, Visitor Policy, Orientation)
   ========================================================================== */
function initModals() {
  const openButtons = document.querySelectorAll('[data-open-modal]');
  const closeButtons = document.querySelectorAll('.modal-close-btn, .modal-overlay');

  openButtons.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const modalId = btn.getAttribute('data-open-modal');
      const modal = document.getElementById(modalId);
      if (modal) {
        modal.classList.add('active');
        document.body.style.overflow = 'hidden';
      }
    });
  });

  closeButtons.forEach(btn => {
    btn.addEventListener('click', (e) => {
      // If clicking inside modal container, don't close unless it's close-btn
      if (btn.classList.contains('modal-overlay') && e.target !== btn) {
        return;
      }
      const activeModal = document.querySelector('.modal-overlay.active');
      if (activeModal) {
        activeModal.classList.remove('active');
        document.body.style.overflow = '';
      }
    });
  });

  // ESC key listener
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const activeModal = document.querySelector('.modal-overlay.active');
      if (activeModal) {
        activeModal.classList.remove('active');
        document.body.style.overflow = '';
      }
    }
  });
}

